/**
 * CRM Home — overview dashboard with stat cards, pipeline chart, and recent activity.
 */

import React, { useState, useEffect } from 'react';
import { useT } from '../../lib/i18n';
import { Card, Spinner, Badge } from '../../components/ui';
import { Building2, Users, DollarSign, TrendingUp, Clock, Mail, Package, Calendar } from '../../lib/icons';
import type { LucideIcon } from '../../lib/icons';
import { formatDate } from '../../lib/utils';
import { fetchCrmStats, fetchCrmPipeline, fetchInteractions } from '../../lib/crm-api';
import type { CrmStats, CrmPipelineItem, CrmInteraction } from '../../lib/crm-api';

// ─── Stage display config ────────────────────────────────

const STAGE_BAR_COLORS: Record<string, string> = {
  draft: 'bg-fg-faint',
  sent: 'bg-info',
  negotiating: 'bg-warning',
  won: 'bg-success',
  lost: 'bg-danger',
  cancelled: 'bg-fg-muted',
};

const STAGE_ORDER = ['draft', 'sent', 'negotiating', 'won', 'lost', 'cancelled'];

// ─── Component ───────────────────────────────────────────

export function CrmHome() {
  const t = useT();
  const [stats, setStats] = useState<CrmStats | null>(null);

  const STAGE_LABELS: Record<string, string> = {
    draft: t('crm.stageDraft'),
    sent: t('crm.stageSent'),
    negotiating: t('crm.stageNegotiating'),
    won: t('crm.stageWon'),
    lost: t('crm.stageLost'),
    cancelled: t('crm.stageCancelled'),
  };
  const [pipeline, setPipeline] = useState<CrmPipelineItem[]>([]);
  const [recentInteractions, setRecentInteractions] = useState<CrmInteraction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCrmStats(), fetchCrmPipeline(), fetchInteractions({ limit: 10 })])
      .then(([s, p, i]) => {
        if (cancelled) return;
        setStats(s);
        setPipeline(p.pipeline);
        setRecentInteractions(i.items);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner className="h-6 w-6 text-fg-faint" />
      </div>
    );
  }

  if (!stats) return null;

  const statCards: Array<{
    label: string;
    value: string | number;
    icon: LucideIcon;
    color: string;
    href?: string;
  }> = [
    {
      label: t('crm.companies'),
      value: stats.total_companies,
      icon: Building2,
      color: 'text-info',
      href: '#/crm/companies',
    },
    {
      label: t('crm.contacts'),
      value: stats.total_contacts,
      icon: Users,
      color: 'text-success',
      href: '#/crm/contacts',
    },
    {
      label: t('crm.products'),
      value: stats.total_products,
      icon: Package,
      color: 'text-fg-secondary',
      href: '#/crm/products',
    },
    {
      label: t('crm.totalDeals'),
      value: stats.total_deals,
      icon: DollarSign,
      color: 'text-warning',
      href: '#/crm/deals',
    },
    {
      label: t('crm.pipelineValue'),
      value: `$${(stats.active_deals_amount || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-primary-fg',
      href: '#/crm/deals',
    },
    {
      label: t('crm.wonRevenue'),
      value: `$${(stats.won_deals_amount || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-success',
      href: '#/crm/deals',
    },
    {
      label: t('crm.followupRadar'),
      value: stats.followups_due,
      icon: Calendar,
      color: 'text-warning',
      href: '#/crm/followups',
    },
  ];

  // Pipeline chart calculations
  const sortedPipeline = STAGE_ORDER.map((stage) => pipeline.find((p) => p.stage === stage)).filter(
    Boolean,
  ) as CrmPipelineItem[];
  const maxCount = Math.max(...sortedPipeline.map((p) => p.count), 1);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-lg font-semibold text-fg">{t('crm.title')}</h1>
          <p className="text-sm text-fg-muted mt-0.5">{t('crm.subtitle')}</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statCards.map((card) => {
            const Icon = card.icon;
            const body = (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className={card.color} />
                  <span className="text-[10px] font-medium text-fg-muted uppercase tracking-wide">{card.label}</span>
                </div>
                <div className="text-xl font-bold text-fg">{card.value}</div>
              </>
            );
            if (card.href) {
              return (
                <a key={card.label} href={card.href} className="block focus:outline-none">
                  <Card className="p-3 cursor-pointer transition-colors hover:bg-surface-muted hover:border-edge-strong">
                    {body}
                  </Card>
                </a>
              );
            }
            return (
              <Card key={card.label} className="p-3">
                {body}
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline chart */}
          <Card className="p-4">
            <h2 className="text-sm font-semibold text-fg mb-4">{t('crm.dealPipeline')}</h2>
            {sortedPipeline.length === 0 ? (
              <p className="text-sm text-fg-faint py-4 text-center">{t('crm.noDealsYet')}</p>
            ) : (
              <div className="space-y-3">
                {sortedPipeline.map((item) => {
                  const barColor = STAGE_BAR_COLORS[item.stage] || 'bg-fg-faint';
                  const pct = (item.count / maxCount) * 100;
                  return (
                    <div key={item.stage}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-fg-secondary font-medium">
                          {STAGE_LABELS[item.stage] || item.stage}
                        </span>
                        <span className="text-xs text-fg-muted">
                          {item.count} · ${item.total_amount.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-5 bg-surface-sunken rounded-md overflow-hidden">
                        <div
                          className={`h-full ${barColor} rounded-md transition-all duration-500 opacity-70`}
                          style={{ width: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Recent interactions */}
          <Card className="p-4">
            <h2 className="text-sm font-semibold text-fg mb-4">{t('crm.recentInteractions')}</h2>
            {recentInteractions.length === 0 ? (
              <p className="text-sm text-fg-faint py-4 text-center">{t('crm.noInteractionsYet')}</p>
            ) : (
              <div className="space-y-2">
                {recentInteractions.map((interaction) => (
                  <div
                    key={interaction.id}
                    className="flex items-start gap-2 py-1.5 border-b border-edge last:border-b-0"
                  >
                    <InteractionIcon type={interaction.type} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            interaction.type === 'email_inbound'
                              ? 'secondary'
                              : interaction.type === 'email_outbound'
                                ? 'default'
                                : 'warning'
                          }
                        >
                          {interaction.type.replace(/_/g, ' ')}
                        </Badge>
                        {interaction.subject && (
                          <span className="text-xs text-fg-secondary truncate" title={interaction.subject}>
                            {interaction.subject}
                          </span>
                        )}
                      </div>
                      {interaction.content_preview && (
                        <p className="text-[11px] text-fg-muted mt-0.5 truncate" title={interaction.content_preview}>
                          {interaction.content_preview}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] text-fg-faint flex-shrink-0">
                      {formatDate(interaction.occurred_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────

function InteractionIcon({ type }: { type: string }) {
  if (type.startsWith('email')) return <Mail size={13} className="text-info flex-shrink-0 mt-0.5" />;
  if (type === 'meeting') return <Users size={13} className="text-warning flex-shrink-0 mt-0.5" />;
  if (type === 'call') return <Clock size={13} className="text-success flex-shrink-0 mt-0.5" />;
  return <Clock size={13} className="text-fg-muted flex-shrink-0 mt-0.5" />;
}
